import React from 'react';
import { FileCheck } from 'lucide-react';
import LegalPageLayout from '../components/legal/LegalPageLayout';
import LegalLink from '../components/legal/LegalLink';
import { legalBodyText, legalLinkClass, legalNoticeBox, legalSectionTitle } from '../components/legal/legalStyles';

const DPAPage: React.FC = () => {
  return (
    <LegalPageLayout
      title="Data Processing Agreement"
      lastUpdated="June 2026"
      icon={<FileCheck className="w-6 h-6 text-white" />}
      iconGradientClassName="from-violet-600 to-purple-600"
      cardTitle="Edyt DPA Summary (B2B)"
      defaultBack={{ href: '/privacy', label: 'Back to Privacy Policy' }}
      footer={{
        href: '/privacy',
        label: 'Back to Privacy Policy',
        buttonGradientClassName:
          'from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700',
      }}
    >
      <div className={legalBodyText}>
        <p className={legalNoticeBox}>
          This page is a product template summarizing our standard B2B data processing terms.
          It is not legal advice. Enterprise customers requiring a countersigned DPA should
          contact us at{' '}
          <a href="mailto:legal@edyt.ai" className={legalLinkClass}>
            legal@edyt.ai
          </a>
          .
        </p>

        <section>
          <h2 className={legalSectionTitle}>Roles</h2>
          <p>
            For customer data uploaded or synced into Edyt, the customer is the data controller
            and Edyt acts as the data processor. Edyt may act as an independent controller for
            account, billing, and product analytics data necessary to operate the service.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>Subject Matter & Duration</h2>
          <p>
            Processing covers customer success intelligence: integration data, AI-generated
            insights, chat sessions, QBR reports, and related metadata. Processing continues
            for the subscription term and until data is deleted per customer instructions or
            our retention policies.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>Subprocessors</h2>
          <p>
            Edyt engages subprocessors listed on our{' '}
            <LegalLink to="/subprocessors">Subprocessors page</LegalLink>. We will provide
            reasonable notice of material changes where required by applicable law.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>Security Measures</h2>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Multi-tenant isolation with tenant-scoped storage</li>
            <li>Encryption in transit and at rest</li>
            <li>Role-based access controls and authenticated API access</li>
            <li>Secret management via cloud provider secret stores</li>
          </ul>
        </section>

        <section>
          <h2 className={legalSectionTitle}>Data Subject Requests & Deletion</h2>
          <p>
            Customers can export organization data and delete integration or account data from
            Settings → Data Management. Edyt provides reasonable assistance for GDPR Articles
            15–20 requests initiated by the customer as controller.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>Breach Notification</h2>
          <p>
            Edyt will notify the customer without undue delay after becoming aware of a personal
            data breach affecting customer data processed on the customer&apos;s behalf, and will
            provide information reasonably required to meet the customer&apos;s regulatory obligations.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>Contact</h2>
          <p>
            For DPA questions or signed agreements:{' '}
            <a href="mailto:legal@edyt.ai" className={legalLinkClass}>
              legal@edyt.ai
            </a>
          </p>
        </section>
      </div>
    </LegalPageLayout>
  );
};

export default DPAPage;
